import { ChangeDetectionStrategy, Component, computed, inject, signal } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatCardModule } from '@angular/material/card';
import { DividerComponent } from '@shared/components/divider.component';
import { TitleContainerComponent } from '@shared/components/title-container.component';
import { WatchlistsTableComponent } from '../watchlists-table/watchlists-table.component';
import { WatchlistsApiService } from '../services/watchlists.service';
import { ManagedTextApiService } from '@shared/services/managed-text-api.service';
import { toSignal } from '@angular/core/rxjs-interop';
import { finalize, map, startWith, switchMap, tap } from 'rxjs';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatGridListModule } from '@angular/material/grid-list';
import { OverlayModule } from '@angular/cdk/overlay';
import { WatchlistCountrySectorHelperComponent } from './country-sector-helper/country-sector-helper.component';
import { ManagedText } from '@shared/models/managed-text.model';
import { CountrySectorHelperTexts, CountrySectorMainTexts } from '../models/country-sector-texts.model';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { FlattenedCountrySector } from '../models/watchlist-country-sector.model';
import { ColDef, RowClickedEvent } from 'ag-grid-community';
import { buildCountrySectorColumnDefs } from './country-sector-watchlist-table.config';
import { CountrySectorDetailDialogComponent } from './country-sector-detail-dialog/country-sector-detail-dialog.component';
import { MatDialog } from '@angular/material/dialog';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { getFilenameFromContentDisposition, saveFile } from '@shared/utils/file.utils';

@Component({
  selector: 'app-watchlist-country-sector',
  imports: [
    TitleContainerComponent,
    MatButtonModule,
    MatIconModule,
    MatTooltipModule,
    DividerComponent,
    WatchlistsTableComponent,
    MatProgressBarModule,
    MatCardModule,
    MatGridListModule,
    OverlayModule,
    WatchlistCountrySectorHelperComponent,
    MatButtonToggleModule,
    ReactiveFormsModule,
    MatProgressSpinnerModule,
  ],
  templateUrl: './country-sector.component.html',
  styleUrl: './country-sector.component.scss',
  providers: [WatchlistsApiService],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class WatchlistCountrySectorComponent {
  readonly textCategory = 'Country Sector Combinations';
  readonly descriptionIndex = 0;
  readonly descriptionTooltipIndex = 1;
  readonly tableNoteIndex = 2;
  readonly restrictedTitleIndex = 3;
  readonly restrictedDescriptionIndex = 4;
  readonly highRiskTitleIndex = 5;
  readonly highRiskDescriptionIndex = 6;
  readonly mediumRiskTitleIndex = 7;
  readonly mediumRiskDescriptionIndex = 8;
  readonly footerNoteIndex = 9;
  readonly SPECIAL_COUNTRIES = ['Syrian Arab Republic', 'North Korea', 'Iran- Islamic Republic of', 'Russia', 'Syrian Arab Republic'];

  private readonly dialog = inject(MatDialog);
  readonly watchlistApiService = inject(WatchlistsApiService);
  countrySectorColumnDefs = signal<ColDef<FlattenedCountrySector>[]>([]);

  isCountrySectorLoading = signal(true);
  isManagedTextLoading = signal(true);
  isLoading = computed(() => this.isCountrySectorLoading() || this.isManagedTextLoading());
  helperPreviewOpened = signal(false);

  readonly managedTextApiService = inject(ManagedTextApiService);

  managedTexts = toSignal(
    this.managedTextApiService.getManagedTextByCategory$(this.textCategory).pipe(
      finalize(() => {
        this.isManagedTextLoading.set(false);
      })
    ),
    { initialValue: [] }
  );

  countrySectorMainTexts = computed<CountrySectorMainTexts>(() => {
    const texts = this.managedTexts();
    return {
      description: this.extractText(this.descriptionIndex, texts) || '',
      descriptionTooltip: this.extractText(this.descriptionTooltipIndex, texts),
      tableNote: this.extractText(this.tableNoteIndex, texts),
    };
  });

  countrySectorHelperTexts = computed<CountrySectorHelperTexts>(() => {
    const texts = this.managedTexts();
    return {
      restrictedTitle: this.extractText(this.restrictedTitleIndex, texts),
      restrictedDescription: this.extractText(this.restrictedDescriptionIndex, texts),
      highRiskTitle: this.extractText(this.highRiskTitleIndex, texts),
      highRiskDescription: this.extractText(this.highRiskDescriptionIndex, texts),
      mediumRiskTitle: this.extractText(this.mediumRiskTitleIndex, texts),
      mediumRiskDescription: this.extractText(this.mediumRiskDescriptionIndex, texts),
      footerNote: this.extractText(this.footerNoteIndex, texts),
    };
  });

  criticalScopeControl = new FormControl(true, { nonNullable: true });
  isCriticalScope = toSignal(this.criticalScopeControl.valueChanges.pipe(startWith(this.criticalScopeControl.value)));

  countrySectorResponse = toSignal(
    this.criticalScopeControl.valueChanges.pipe(
      startWith(this.criticalScopeControl.value),
      tap(() => {
        this.isCountrySectorLoading.set(true);
      }),
      switchMap(value =>
        this.watchlistApiService.getWatchlistsCountrySector$(value).pipe(
          map(data => ({
            ...data,
            countrySectorData: data.countrySectorData.map(item => ({
              ...item,
              countryName: this.SPECIAL_COUNTRIES.includes(item.countryName) ? `${item.countryName} *` : item.countryName,
            })) as FlattenedCountrySector[],
          })),
          tap(data => {
            this.countrySectorColumnDefs.set(buildCountrySectorColumnDefs(data.industrySegmentHeaders));
          }),
          finalize(() => {
            this.isCountrySectorLoading.set(false);
          })
        )
      )
    )
  );

  isLoadingExport = signal(false);

  private extractText(index: number, data: ManagedText[]): string {
    return data.length > index ? data[index]?.description : '';
  }

  onRowClicked(event: RowClickedEvent<FlattenedCountrySector>): void {
    if (event.data) {
      this.openProjectDetailDialog(event.data);
    }
  }

  private openProjectDetailDialog(project: FlattenedCountrySector): void {
    this.dialog.open(CountrySectorDetailDialogComponent, {
      data: project,
      width: '70vw',
      maxWidth: '900px',
      height: 'auto',
      maxHeight: '70vh',
      disableClose: false,
      autoFocus: true,
    });
  }

  exportExcel(): void {
    this.isLoadingExport.set(true);
    this.watchlistApiService
      .exportWatchlistsCountrySector$(this.criticalScopeControl.value)
      .pipe(
        tap(response => {
          const contentDisposition = response.headers.get('Content-Disposition');
          const filename = getFilenameFromContentDisposition(contentDisposition) ?? 'downloaded-file';
          if (response.body) {
            saveFile(response.body, filename);
          }
        }),
        finalize(() => {
          this.isLoadingExport.set(false);
        })
      )
      .subscribe();
  }
}
